import { couponIdOf, entryKey, idOnTheBill, sameOffer } from "./identity";
import type { OfferKey } from "./identity";
import { getPartner } from "./partners";
import type { BillDiscount, DiscountOffer, Sale } from "./types";
import { DISCOUNT_CLASS, DISCOUNT_MODE, DISCOUNT_TYPE, EXTERNAL_SOURCE } from "./vocabulary";
import type { EntryKind } from "./vocabulary";

/**
 * The bill discount entry: the `billDiscountList` item written for one offer.
 *
 * 🔴 **It carries only the keys something reads.** The pricing engine rebuilds the bill's discount
 * list on every price, so whatever else an entry carries is gone after the terminal's first price;
 * the server, the partners and the reports only ever see what the engine kept. The keep list,
 * per kind of entry, was measured against the engine, the save and the partners and confirmed by
 * the user (docs/architecture/discounts/bill-entry-readers.md §5; DISCOUNT_V2_PLAN.md §9.10,
 * 9 and 10). `ENTRY_KEYS` below IS that list, and `__tests__/entry.test.ts` fails when it and the
 * document's table differ.
 *
 * Pure: offer and context in, a new entry out. Writing it onto a sale is the session's runner (`discounts/session/run.ts` › `withChanges`).
 */

/** The keep list: an entry has the `common` keys plus its kind's (bill-entry-readers.md §5). */
export const ENTRY_KEYS = {
  common: [
    "discountId",
    "discountCode",
    "discountCodeDisplayName",
    "description",
    "dclass",
    "mode",
    "discountType",
    "discountRate",
    "source",
    "useLoyalty",
    "authenticateCustomer",
    "notes",
    "remarks",
  ],
  edited: ["isOverridden"],
  catalogueDeal: ["onlyOnce"],
  typedCoupon: [
    "couponCode",
    "couponProvider",
    "requestId",
    "couponId",
    "campaignName",
    "referenceId",
    "blockId",
    "blockedUntil",
  ],
  manualDiscount: [
    "itemIds",
    "categoryIds",
    "subCategoryIds",
    "categorySubCategoryIds",
    "maxDiscountAmount",
    "maximumDiscountAmount",
    "applyOnLeastExpensiveItem",
    "applyOnMostExpensiveItem",
    "applyOnlyOnSelectedItems",
    "BuyX",
    "BuyXGetX",
    "BuyXGetY",
    "BuyXGetYVariantType",
    "BuyXReplaceY",
    "SlabDiscount",
    "paymentMode",
    "excludeFromOrderLevelDeals",
    "membershipIds",
    "loyaltyPoints",
  ],
  partnerOffer: [
    "isExternal",
    "couponProvider",
    "couponCode",
    "referenceId",
    "blockId",
    "blockedUntil",
    "requestId",
  ],
  reward: ["onlyOnce", "loyaltyPoints", "applyOnlyOnSelectedItems"],
} as const satisfies Record<"common" | "edited" | EntryKind, readonly string[]>;

/** What the entry needs that does not come from the offer itself. */
export interface EntryContext {
  sale: Sale;
  /** The active coupon provider's `metaId`: a partner offer that names no provider is its. */
  activeProviderMetaId?: string;
  /** `discountStore.discountInfo`: the coupon check's last answer. */
  appliedDiscountInfo?: Record<string, any>;
  /** Editing an already-applied discount: it is marked overridden. */
  isEditMode?: boolean;
  /** `"voucher"` when the offer came from the coupon check (`/coupon/verify`), not a list. */
  source?: string;
  /** A partner reference id from this conversation, preferred over the stored one. */
  referenceId?: string;
  /** The stored partner reference id (`LoyaltyStore.externalRefId`). */
  storedReferenceId?: string;
  /** How long the partner has reserved the coupon (`redemptionInfo.data.blockedUntil`). */
  blockedUntil?: string;
  /** The partner's handle for that reservation, from the verify answer's top level. */
  blockId?: string;
}

const isPartnerOffer = (offer: DiscountOffer): boolean =>
  offer.isExternal === true || offer.source === EXTERNAL_SOURCE || getPartner(offer.couponProvider).isExternalLoyalty;

/** Which keep list an offer's entry follows (bill-entry-readers.md §2). */
export function entryKindOf(offer: DiscountOffer, { typedCode = false }: { typedCode?: boolean } = {}): EntryKind {
  if (isPartnerOffer(offer)) return "partnerOffer";
  if (typedCode) return "typedCoupon";
  if (offer.dclass === DISCOUNT_CLASS.discount && offer.mode === DISCOUNT_MODE.manual) return "manualDiscount";
  if (offer.isLoyaltyReward === true) return "reward";
  return "catalogueDeal";
}

/** Copies each named key that has a value; an absent key stays absent rather than `undefined`. */
function copyKeys(target: Record<string, any>, source: Record<string, any>, keys: readonly string[]): void {
  keys.forEach((key) => {
    if (source[key] !== undefined) target[key] = source[key];
  });
}

/** Sets a key only when there is a value, so the engine never receives an empty field. */
function setIfPresent(target: Record<string, any>, key: string, value: unknown): void {
  if (value !== undefined && value !== null && value !== "") target[key] = value;
}

/**
 * The bill entry for this offer.
 *
 * - **a partner offer** keeps the offer exactly as the partner sent it (the engine may price an
 *   external deal from the entry itself), plus the conversation's ids;
 * - **every other kind** is built from the keep list alone.
 *
 * ⚠️ The values below are the ones v1 wrote; only the set of keys is new (R2.1). Where a partner
 * names no `couponProvider`, it falls back to the coupon check's provider, then the active one,
 * as web v1's `applyDiscount` does. Moving each source's own answer into its own source is R2.4's.
 */
export function buildEntry(offer: DiscountOffer, context: EntryContext): BillDiscount {
  const {
    activeProviderMetaId,
    appliedDiscountInfo = {},
    isEditMode = false,
    source,
    referenceId,
    storedReferenceId,
    blockedUntil,
    blockId,
  } = context;
  const typedCode = source === "voucher";
  const kind = entryKindOf(offer, { typedCode });

  const entry: Record<string, any> = kind === "partnerOffer" ? { ...offer } : {};
  copyKeys(entry, offer, ENTRY_KEYS.common);
  if (kind !== "partnerOffer") copyKeys(entry, offer, ENTRY_KEYS[kind]);
  // The catalogue keys its offers by `mid`; the bill keys them by `discountId` (identity.ts).
  setIfPresent(entry, "discountId", idOnTheBill(offer));

  if (isEditMode) entry.isOverridden = true;

  if (kind === "partnerOffer" || kind === "typedCoupon") {
    const couponProvider = typedCode
      ? appliedDiscountInfo?.sourceInfo?.provider
      : offer.couponProvider || appliedDiscountInfo?.sourceInfo?.provider || activeProviderMetaId;
    setIfPresent(entry, "couponProvider", couponProvider);
    setIfPresent(entry, "referenceId", referenceId || storedReferenceId);
    setIfPresent(entry, "blockedUntil", blockedUntil);
    setIfPresent(entry, "blockId", blockId);
  }

  if (typedCode) {
    setIfPresent(entry, "couponCode", appliedDiscountInfo?.couponCode);
    // Decision 10: the campaign reaches the saved sale (the engine copies both into it).
    setIfPresent(entry, "couponId", couponIdOf(appliedDiscountInfo));
    setIfPresent(entry, "campaignName", appliedDiscountInfo?.campaignName);
  }

  // A partner that correlates its redemption by a request id (the profile's `sendsRequestId`).
  if (getPartner(entry.couponProvider).sendsRequestId) {
    if (offer.mode === DISCOUNT_MODE.coupon) {
      // shortcut: v1 takes the code from the coupon check's last answer even for a listed offer; R2.4's sources pass their own.
      entry.couponCode = appliedDiscountInfo?.couponCode;
      if (entry.couponCode === undefined) delete entry.couponCode;
    }
    setIfPresent(entry, "requestId", appliedDiscountInfo?.requestId || entry.referenceId);
  }

  return entry as BillDiscount;
}

/**
 * `billDiscountList` without the entries of these keys (v1 `removeDiscountFromCart`): an automatic
 * deal is not deleted but suppressed at `priority: -1`, or pricing would put it back. By identity
 * (`entryKey` and `sameOffer`); an entry that has no key is never matched.
 */
export function withoutEntries(billDiscountList: readonly BillDiscount[], keys: readonly OfferKey[]): BillDiscount[] {
  return billDiscountList.flatMap((entry): BillDiscount[] => {
    if (!keys.some((key) => sameOffer(entryKey(entry), key))) return [entry];
    return entry.mode === DISCOUNT_MODE.auto ? [{ ...entry, priority: -1 }] : [];
  });
}

/* ── the item discount: one line's manual discount (decisions 30, 64) ─────────────────────────── */

/**
 * The line entry's keep list (docs/architecture/discounts/item-discount-readers.md §5). Unlike the
 * bill's list, the engine never rebuilds a line's `itemDiscountList`, so whatever an entry carries
 * reaches the server; it copies `mode`, `notes`, `remarks` and `authenticateCustomer` into the
 * bill's summary, where the approval limit and the customer check read them.
 */
export const LINE_ENTRY_KEYS = [
  "dclass",
  "mode",
  "discountId",
  "discountCode",
  "discountType",
  "discountRate",
  "categoryIds",
  "notes",
  "remarks",
  "isOverridden",
  "authenticateCustomer",
] as const;

/** What the cashier changed in the item discount's edit popup. */
export interface LineDiscountEdit {
  discountType?: string;
  discountRate?: number;
  notes?: string;
}

/**
 * The `itemDiscountList` entry for a catalogue manual discount on one line. v1 cloned the whole
 * catalogue record (decision 64 keeps only the readers' keys); an edit overrides the rate, the type
 * and the note and marks the entry overridden, as v1's edit popup does.
 */
export function buildLineEntry(record: DiscountOffer, edit?: LineDiscountEdit): BillDiscount {
  const entry: Record<string, any> = {};
  copyKeys(entry, record, LINE_ENTRY_KEYS);
  // The catalogue keys its offers by `mid`; the line, like the bill, by `discountId` (identity.ts).
  setIfPresent(entry, "discountId", idOnTheBill(record));
  if (edit) {
    copyKeys(entry, edit as Record<string, any>, ["discountType", "discountRate", "notes"]);
    entry.isOverridden = true;
  }
  return entry as BillDiscount;
}

/**
 * Is this bill entry a redeemed-loyalty-points discount, by TODAY's rule: carries `loyaltyPoints`
 * and is of class `Discount`? Such entries carry neither `discountId` nor `mid`, so they are
 * identified structurally, as v1's `helpers/loyaltyHelper/shared.ts` › `isLoyaltyPointDiscountType`
 * does. Returns a real boolean; v1's returns the points number.
 *
 * Today's rule, kept for the live readers and the write path until cutover (decision 59);
 * V1_DUPLICATES.md D11. Discount v2 reads `isPointsLine` instead.
 */
export const isPointsLineAsV1 = (entry: any): boolean =>
  !!entry?.loyaltyPoints && entry?.dclass === DISCOUNT_CLASS.discount;

/**
 * Is this bill entry the points line, AS THE BACKEND WRITES IT (decision 55)? The entry is a manual,
 * absolute discount of class `Discount` that does not use loyalty and carries points above zero
 * (the backend's refund finds it so: `sale_loyalty_service.js` 480, `dclass === 'Discount' &&
 * mode === 'Manual' && discountType === 'Absolute' && !useLoyalty && loyaltyPoints > 0`), and
 * whose `discountCode` is one of the active loyalty partners' `meta.discountCode` (the line is
 * written under it, 346-367; cancel and void match it against the partners' codes, 4030, 4720).
 * So a reward line or a manual discount that merely carries points is not the points line, and a
 * points deal (`useLoyalty`) is not either. `pointsLineCodes` is `pointsLineCodes(providers)`.
 */
export const isPointsLine = (entry: any, pointsLineCodes: readonly string[]): boolean =>
  entry?.dclass === DISCOUNT_CLASS.discount &&
  entry?.mode === DISCOUNT_MODE.manual &&
  entry?.discountType === DISCOUNT_TYPE.absolute &&
  !entry?.useLoyalty &&
  Number(entry?.loyaltyPoints) > 0 &&
  typeof entry?.discountCode === "string" &&
  pointsLineCodes.includes(entry.discountCode);

/**
 * The codes a points line may be written under: each configured loyalty partner's
 * `meta.discountCode` (the provider's marketing document), empty ones left out. A code is what
 * the backend matches on (`sale_loyalty_service.js` 4030, 4720), so a provider without one writes
 * no points line.
 */
export const pointsLineCodes = (providers: readonly { meta?: { discountCode?: string } }[]): string[] =>
  providers.map((provider) => provider?.meta?.discountCode).filter((code): code is string => !!code);

/** A loyalty partner as far as its points line is concerned: its `metaId` and the code the line is written under. */
export interface PointsLinePartner {
  metaId?: string;
  meta?: { discountCode?: string };
}

/**
 * The `metaId` of the partner whose points line this entry is, or `undefined` when it is not a points
 * line (`isPointsLine`, decision 55). The points line often has no id of its own (no `discountId`, no
 * `couponCode`), so the partner is read from the one thing it does carry, the code it is written under.
 */
export function pointsLinePartner(entry: any, partners: readonly PointsLinePartner[]): string | undefined {
  if (!isPointsLine(entry, pointsLineCodes(partners))) return undefined;
  return partners.find((partner) => partner.meta?.discountCode === entry.discountCode)?.metaId;
}
